/**
 * useUsersList — TanStack Query hook over `GET /organizations/:slug/users`.
 *
 * The org-roster endpoint is the data source for `/users` in the admin
 * portal. Phase 1 of SCRUM-108 swaps the page from the single-self-row
 * placeholder (powered by `useCurrentMembershipView`) to the real list.
 *
 * Status surface (closed):
 *   - 'loading'   — request in flight, OR `useCurrentOrg` has not yet
 *                   resolved a membership (the hook short-circuits
 *                   `enabled` so no empty-slug request hits the wire).
 *   - 'ready'     — 200 OK; `data`, `total` populated.
 *   - 'forbidden' — 403; deliberate distinct branch so the page can
 *                   render a permission-denied panel rather than the
 *                   generic Retry affordance. The caller must already
 *                   have `identity.users.read` to land on the route,
 *                   so a 403 here means BE permission state diverged
 *                   from the cached `/auth/me` payload (recently
 *                   revoked, etc.).
 *   - 'error'     — anything else (5xx, network, schema drift).
 *
 * Query-key composition includes the org slug, filter object, sort
 * tuple, page, and perPage. Object identity is stable across renders
 * inside React's cache; callers may pass freshly-built objects without
 * thrashing the cache as long as the contents are equal — TanStack
 * Query compares keys structurally.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import type { OrgMemberRow } from './types.js';

export interface UsersListFilters {
  readonly q?: string;
  readonly role?: string;
  readonly status?: 'active' | 'pending_verification' | 'suspended';
  readonly groupId?: string;
}

export interface UsersListSort {
  readonly field: 'email' | 'role' | 'status' | 'last_seen';
  readonly dir: 'asc' | 'desc';
}

export interface UsersListInput {
  readonly filters: UsersListFilters;
  readonly sort: UsersListSort;
  /** 1-indexed page number. BE rejects page < 1. */
  readonly page: number;
  /** BE caps at 100; Phase 1 defaults to 50. */
  readonly perPage: number;
}

export type UseUsersListResult =
  | {
      readonly status: 'loading';
      readonly data?: undefined;
      readonly total?: undefined;
      readonly page: number;
      readonly perPage: number;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'ready';
      readonly data: readonly OrgMemberRow[];
      readonly total: number;
      readonly page: number;
      readonly perPage: number;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'forbidden';
      readonly data?: undefined;
      readonly total?: undefined;
      readonly page: number;
      readonly perPage: number;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'error';
      readonly data?: undefined;
      readonly total?: undefined;
      readonly page: number;
      readonly perPage: number;
      readonly refetch: () => void;
    };

/**
 * Internal symbol used by the queryFn to distinguish a 403 response
 * from any other error. We can't `throw` and rely on TanStack's
 * default error path because the page needs a permission-denied
 * branch that is NOT the retry/error affordance.
 */
const FORBIDDEN = Symbol('users-list:forbidden');

interface RosterEnvelope {
  readonly data: readonly RosterDto[];
  readonly page: number;
  readonly per_page: number;
  readonly total: number;
}

interface RosterDto {
  readonly id: string;
  readonly email: string;
  readonly firstName?: string | null;
  readonly lastName?: string | null;
  readonly status: 'active' | 'pending_verification' | 'suspended';
  readonly roles?: ReadonlyArray<{ slug: string; displayName: string }>;
  readonly lastSeenAt?: string | null;
}

function buildQueryParams(
  input: UsersListInput,
): Record<string, string | number> {
  const out: Record<string, string | number> = {
    sort: input.sort.field,
    dir: input.sort.dir,
    page: input.page,
    per_page: input.perPage,
  };
  if (input.filters.q !== undefined && input.filters.q.length > 0) {
    out.q = input.filters.q;
  }
  if (input.filters.role !== undefined && input.filters.role.length > 0) {
    out.role = input.filters.role;
  }
  if (input.filters.status !== undefined) {
    out.status = input.filters.status;
  }
  if (
    input.filters.groupId !== undefined &&
    input.filters.groupId.length > 0
  ) {
    out.groupId = input.filters.groupId;
  }
  return out;
}

function projectRow(dto: RosterDto): OrgMemberRow {
  const primary = dto.roles?.[0];
  return {
    userId: dto.id,
    email: dto.email,
    firstName: dto.firstName ?? null,
    lastName: dto.lastName ?? null,
    status: dto.status,
    primaryRoleSlug: primary?.slug ?? null,
    primaryRoleDisplayName: primary?.displayName ?? null,
    lastSeenAt: dto.lastSeenAt ?? null,
  };
}

export function useUsersList(input: UsersListInput): UseUsersListResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const query = useQuery({
    queryKey: [
      'identity',
      'users',
      orgSlug,
      input.filters,
      input.sort,
      input.page,
      input.perPage,
    ],
    enabled: orgSlug !== null,
    retry: false,
    // Retain the previous page's data while the next query is in flight
    // so the page does not flip into a 'loading' branch on every
    // keystroke / filter change. Without this, UsersListPage unmounts
    // the search row when isPending toggles true, which destroys focus
    // on the search input mid-typing. The `keepPreviousData` sentinel
    // is the TanStack v5 idiom (replaces v4's `keepPreviousData: true`).
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await apiClient.GET('/organizations/{slug}/users', {
        params: {
          path: { slug: orgSlug as string },
          query: buildQueryParams(input) as never,
        },
      });
      if (result.response.status === 403) {
        return FORBIDDEN;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/users failed with status ${result.response.status}`,
        );
      }
      return result.data as RosterEnvelope;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (orgSlug === null || query.isPending) {
    return {
      status: 'loading',
      page: input.page,
      perPage: input.perPage,
      refetch,
    };
  }
  if (query.isError) {
    return {
      status: 'error',
      page: input.page,
      perPage: input.perPage,
      refetch,
    };
  }
  if (query.data === FORBIDDEN) {
    return {
      status: 'forbidden',
      page: input.page,
      perPage: input.perPage,
      refetch,
    };
  }
  const envelope = query.data;
  return {
    status: 'ready',
    data: envelope.data.map(projectRow),
    total: envelope.total,
    page: envelope.page,
    perPage: envelope.per_page,
    refetch,
  };
}
